import { StyledText, GeneralSelect, MultiValue } from '@clickcease/cc-client-components-lib'
import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { WrapperWithButtons } from '../../../../../../../components/Elements/WrapperWithButtons/WrapperWithButtons'
import { useGetUserDomains } from '../../../../../../DomainPresentation/api/getUserDomains'
import { useGetFacebookPixels } from '../../../../../api/getFacebookPixels'
import {
  DomainPixelObject,
  useSetFacebookPixelsToDomains,
} from '../../../../../api/setFacebookPixelsToDomains'

import { DomainSelectionContainer, IdsSelectionRow } from './DomainToPixel.styled'

export type ReactSelectOption = {
  label: string
  value: number
}

export const DomainToPixel = ({
  onSubmit,
  accountId,
  onBack,
  isBackDisabled,
}: {
  onSubmit: () => void
  accountId: number
  onBack: () => void
  isBackDisabled?: boolean
}) => {
  const { t } = useTranslation('PMPlatforms')
  const { data } = useGetUserDomains({})
  const [selectedDomains, setSelectedDomains] = useState<{ [key: string]: ReactSelectOption[] }>({})

  const { data: pixelData } = useGetFacebookPixels({ connectionId: accountId })

  const pixels = useMemo(() => {
    return pixelData instanceof Error || !pixelData?.pixels ? [] : pixelData.pixels
  }, [pixelData])

  const domains = useMemo(() => (!data?.domains ? [] : data.domains), [data])

  const selectedPixelOptions = useMemo(() => {
    const result: ReactSelectOption[] = []
    const selectedPixelsPerDomain: { [key: string]: ReactSelectOption[] } = {}

    pixels.forEach((px) => {
      if (px.selected) {
        result.push({ value: px.id, label: px.name })
        // set selected pixels form API response
        if (px.domainIds?.length) {
          px.domainIds.forEach((domainId) => {
            if (selectedPixelsPerDomain[domainId]) {
              selectedPixelsPerDomain[domainId].push({ value: px.id, label: px.name })
            } else {
              selectedPixelsPerDomain[domainId] = [{ value: px.id, label: px.name }]
            }
          })
        }
      }
    }, [])

    setSelectedDomains(selectedPixelsPerDomain)
    return result
  }, [pixels])

  const { mutate, isLoading } = useSetFacebookPixelsToDomains()

  const setPixelToDomain = (domainId: number) => (selected: MultiValue<ReactSelectOption>) => {
    if (selected) {
      setSelectedDomains((prev) => ({
        ...prev,
        [domainId]: selected,
      }))
    }
  }

  const selectionToApiObject = (selected: { [key: string]: ReactSelectOption[] }) => {
    const result: DomainPixelObject[] = []
    Object.keys(selected).forEach((domainId) => {
      selected[domainId].forEach((pixelId) => {
        result.push({ domainId: Number(domainId), pixelId: pixelId.value })
      })
    })
    return result
  }

  const handleSubmit = () => {
    mutate(
      {
        connectionId: accountId,
        pixels: selectionToApiObject(selectedDomains),
      },
      {
        onSuccess: (data) => {
          if (!(data instanceof Error)) {
            onSubmit()
          }
        },
      }
    )
  }

  return (
    <WrapperWithButtons
      onGhostButtonClick={onBack}
      ghostButtonText={t('back')}
      primaryButtonText={t('next-button')}
      onPrimaryButtonClick={handleSubmit}
      isPrimaryButtonLoading={isLoading}
      isPrimaryButtonDisabled={!Object.keys(selectedDomains).length}
      isGhostButtonDisabled={isBackDisabled}
    >
      <StyledText variant={'baseLight'}>
        {t('meta-connection-wizard.domain-to-pixel-explainer')}
      </StyledText>
      <IdsSelectionRow header css={{ marginTop: '$4' }}>
        <StyledText variant={'baseBold'}>Domain</StyledText>
        <StyledText variant={'baseBold'}>Pixel ID</StyledText>
      </IdsSelectionRow>
      <DomainSelectionContainer>
        {domains.map((domain) => (
          <IdsSelectionRow key={domain.address}>
            <StyledText>{domain.address}</StyledText>

            <GeneralSelect
              value={selectedDomains[domain.id] ?? []}
              options={selectedPixelOptions}
              onChange={setPixelToDomain(domain.id)}
              isMulti
              hideSelectedOptions={false}
            />
          </IdsSelectionRow>
        ))}
      </DomainSelectionContainer>
    </WrapperWithButtons>
  )
}
